import React, { useEffect, useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faSearch,
  faRefresh,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faEnvelope,
  faPhone,
  faTag,
} from '@fortawesome/free-solid-svg-icons'
import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import mobileSubheader from '../_layout/elements/mobile-subheader'
import affiliateService from '../../../services/affilate.serrvice'

// === Interfaces ===
interface ReferredUser {
  _id: string
  fullName?: string
  username?: string
  email?: string
  phone?: string
  createdAt: string
  referralCode?: string
  referrerCode?: string
  status?: string
  isLogin?: boolean

}

// Pagination interface
interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

const AffiliateUser: React.FC = () => {
  const [users, setUsers] = useState<ReferredUser[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  })

  // 🔹 Load referred users from backend
  useEffect(() => {
    const loadAffiliateUsers = async () => {
      setLoading(true)
      try {
        const res = await affiliateService.getAffiliateUsers()
        if (res.data && res.data.code === 200 && !res.data.error) {
          const fetchedUsers: ReferredUser[] = res.data.data?.users || []
          setUsers(fetchedUsers)
        } else {
          toast.error(res.data?.message || 'Failed to load affiliate users')
        }
      } catch (err) {
        console.error('Failed to load affiliate users:', err)
        toast.error('Failed to fetch affiliate users')
      } finally {
        setLoading(false)
      }
    }

    loadAffiliateUsers()
  }, [])

  // 🔹 Apply search + pagination
  const filteredUsers = useMemo(() => {
    let filtered = users
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = users.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(term) ||
          u.username?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.phone?.includes(term) ||
          u.referrerCode?.toLowerCase().includes(term)
      )
    }
    return filtered
  }, [users, searchTerm])

  const paginatedUsers = useMemo(() => {
    const totalCount = filteredUsers.length
    const totalPages = Math.ceil(totalCount / pageSize)
    const start = (currentPage - 1) * pageSize
    const paginated = filteredUsers.slice(start, start + pageSize)

    setPagination({
      currentPage,
      totalPages,
      totalCount,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1,
    })

    return paginated
  }, [filteredUsers, currentPage, pageSize])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  return (
    <>
      {mobileSubheader.subheaderdesktopadmin('Affiliate Referred Users')}
      <div className='container-fluid'>
        <div className='row'>
          <div className={!isMobile ? 'col-md-12 mt-1' : 'col-md-12 padding-custom'}>
            <div className='card' style={{ marginTop: '10px' }}>
              <div className='card-header bg-primary text-white'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center'>
                    <FontAwesomeIcon icon={faUsers} className='me-2' />
                    <h5 className='mb-0'>My Referred Users</h5>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='badge bg-light text-dark'>
                      Total: {pagination.totalCount}
                    </span>
                    <button
                      className='btn btn-light btn-sm'
                      onClick={() => window.location.reload()}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faRefresh} className={loading ? 'fa-spin' : ''} />
                    </button>
                  </div>
                </div>
              </div>

              <div className='card-body'>
                {/* 🔎 Search Bar */}
                <div className='row mb-4'>
                  <div className='col-12'>
                    <form onSubmit={handleSearch} className='row g-3'>
                      <div className='col-md-6'>
                        <div className='input-group'>
                          <span className='input-group-text'>
                            <FontAwesomeIcon icon={faSearch} />
                          </span>
                          <input
                            type='text'
                            className='form-control'
                            placeholder='Search referred users...'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className='col-md-6 d-flex'>
                        <button type='submit' className='btn btn-primary me-2' disabled={loading} style={{ width: '29%', marginRight: '8px' }}>
                          <FontAwesomeIcon icon={faSearch} className='me-2' />
                          Search
                        </button>
                        <button
                          type='button'
                          className='btn btn-outline-secondary'
                          onClick={() => {
                            setSearchTerm('')
                            setCurrentPage(1)
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* 📋 Table */}
                <div className='table-responsive'>
                  {loading ? (
                    <div className='text-center py-5'>Loading...</div>
                  ) : paginatedUsers.length === 0 ? (
                    <div className='text-center py-5 text-muted'>
                      No referred users found
                    </div>
                  ) : (
                    <table className='table table-hover'>
                      <thead className='table-dark'>
                        <tr>
                          <th>User</th>
                          <th>Referral Code</th>
                          <th>Joined</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user,index) => (
                          <tr key={user._id}>
                            <td>
                              <div className='fw-bold text-primary'>{user.fullName}-{index+1}</div>
                            </td>

                            <td>
                              <span className='badge bg-info text-dark'>
                                <FontAwesomeIcon icon={faTag} className='me-1' />
                                {user.referrerCode}
                              </span>
                            </td>
                            <td>
                              <FontAwesomeIcon icon={faCalendarAlt} className='me-1 text-muted' />
                              <small>{formatDate(user.createdAt)}</small>
                            </td>
                            <td>
                              <span
                                className={`badge ${user.isLogin ? 'bg-success' : 'bg-secondary'
                                  }`}
                              >
                                {user.isLogin ? 'Active' : 'Deactive'}
                              </span>
                            </td>

                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* 📄 Pagination */}
                {pagination.totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center mt-4'>
                    <div className='text-muted'>
                      Showing {(currentPage - 1) * pageSize + 1}–
                      {Math.min(currentPage * pageSize, pagination.totalCount)} of{' '}
                      {pagination.totalCount}
                    </div>
                    <nav>
                      <ul className='pagination mb-0'>
                        <li className={`page-item ${!pagination.hasPrevPage ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={!pagination.hasPrevPage}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                        </li>
                        {Array.from({ length: pagination.totalPages }, (_, i) => (
                          <li
                            key={i + 1}
                            className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                          >
                            <button className='page-link' onClick={() => setCurrentPage(i + 1)}>
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${!pagination.hasNextPage ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={!pagination.hasNextPage}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AffiliateUser
